<script lang="ts" setup>
import { navigateTo } from "#app";
import axios from "axios";
import { ref } from "vue";
import ButtonPrimary from "~/components/ButtonPrimary.vue";
import CheckboxTag from "~/components/form/CheckboxTag.vue";
import InputAddressTag from "~/components/form/InputAddressTag.vue";
import InputTag from "~/components/form/InputTag.vue";
import LabelTag from "~/components/form/LabelTag.vue";
import SelectTag from "~/components/form/SelectTag.vue";
import TextareaTag from "~/components/form/TextareaTag.vue";
import TrialInfo from "~/components/freeTrial/TrialInfo.vue";
import H3Heading from "~/components/H3Heading.vue";
import { useHelper } from "~/composables/helper";
import { STATES } from "~/constants/states";
import type ProductType from "~~/types/Product";

const config = useRuntimeConfig();

// Properties
const props = defineProps({
    product: {
        type: Object as PropType<ProductType>,
        default: null,
    },
});

// Data
const { user } = storeToRefs(useUserStore());

const { getApiError } = useHelper();
const errorMsg = ref("");

const form = ref({
    productId: props.product.c_id,
    userId: 0,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: {
        name: "",
        street: "",
        city: "",
        state: "",
        zip: "",
    },
    referral: "",
    comments: "",
    agree: false,
    consent_to_receive_email: true,
    trialType: "",
});
const sampleTypeOptions = ref({
    print: "Print Workbook",
    digital: "Digital access to testing & eBook",
    both: "Both",
});

const loading = ref(true);
const processing = ref(false);
const missingUserInfo = ref(false);

onMounted(async () => {
    // Auto fill user personal info in case user does not have organization info yet
    await useUserStore().fetchUser(false);
    user.value = useUserStore().user;
    if (user.value) {
        form.value.firstName = user.value?.first_name;
        form.value.lastName = user.value.last_name;
        form.value.email = user.value.email;
        form.value.phone = user.value.phone ? user.value.phone : (user.value.organization?.phone ?? "");
        form.value.userId = user.value?.id;
        // Organization info
        form.value.organization.name = user.value.organization?.name ?? "";
        form.value.organization.street = user.value.organization?.street ?? "";
        form.value.organization.city = user.value.organization?.city ?? "";
        form.value.organization.state = user.value.organization?.state ?? "";
        form.value.organization.zip = user.value.organization?.zip ?? "";
    }

    if (
        !form.value.firstName ||
        !form.value.lastName ||
        !form.value.email ||
        !form.value.phone ||
        !form.value.organization.name ||
        !form.value.organization.street ||
        !form.value.organization.city ||
        !form.value.organization.state ||
        !form.value.organization.zip
    ) {
        missingUserInfo.value = true;
    }

    loading.value = false;
});

// Functions
async function processFreeTrial() {
    try {
        errorMsg.value = "";
        processing.value = true;

        const { data } = await axios
            .post(config.public.STATAMIC_API + `/free-trial/${form.value.productId}`, form.value)
            .finally(() => {
                processing.value = false;
            });

        navigateTo(
            `/free-trial/thank-you?c=${data["code"]}&rid=${data["resource_id"]}&pid=${form.value.productId}&type=${form.value.trialType}`,
        );
    } catch (e: any) {
        console.log("Error:", e);
        errorMsg.value = getApiError(e);
    }
}
</script>

<template>
    <div>
        <div
            v-if="!user"
            class="mb-2"
        >
            Already have an account?
            <a
                href="/login"
                class="text-abc-blue font-bold"
                >Log in</a
            >
        </div>

        <div
            v-show="loading"
            class="text-center"
        >
            <img
                src="/images/spinnersBlocksWave.svg"
                alt="Spinner"
                class="mx-auto w-20"
            />
            Loading...
        </div>
        <form
            v-show="!loading"
            id="form-free-trial"
            @submit.prevent="processFreeTrial"
        >
            <!-- Form for logged in user + has organization -->
            <div
                v-if="user && !missingUserInfo"
                class="lg:flex"
            >
                <TrialInfo
                    :product="product"
                    :trial-type="form.trialType"
                    class="flex-auto"
                />

                <div class="bg-abc-gray mt-6 w-1/4 flex-none rounded-lg p-4 lg:mt-0 lg:ml-6">
                    <H3Heading
                        line-height="leading-h4"
                        class="mb-4 text-center md:text-left"
                    >
                        Order total: FREE
                    </H3Heading>
                    <div class="mb-6">
                        <LabelTag for="sampleType">Trial Type *</LabelTag>
                        <SelectTag
                            id="sampleType"
                            v-model="form.trialType"
                            class="w-full"
                            round-size="rounded-sm"
                            required
                        >
                            <option
                                v-for="(val, key) in sampleTypeOptions"
                                :key="key"
                                :value="key"
                            >
                                {{ val }}
                            </option>
                        </SelectTag>
                    </div>

                    <div>
                        <CheckboxTag
                            id="agree"
                            v-model="form.agree"
                            :value="true"
                            :required="true"
                            line-height="leading-tight"
                            class="mb-4"
                        >
                            I agree to the terms and conditions of the ABC free trial
                        </CheckboxTag>
                        <CheckboxTag
                            id="consent"
                            v-model="form.consent_to_receive_email"
                            :value="true"
                            line-height="leading-tight"
                        >
                            I consent to receive periodic emails and promotion from American Book Company.
                        </CheckboxTag>
                    </div>

                    <div
                        v-if="errorMsg"
                        class="mt-8 text-center"
                        v-html="errorMsg"
                    ></div>
                    <ButtonPrimary
                        type="submit"
                        :disabled="processing"
                        class="mt-8 w-full"
                        >Start my free trial
                    </ButtonPrimary>
                </div>
            </div>

            <!-- Form for guest -->
            <div v-else>
                <TrialInfo
                    :product="product"
                    :trial-type="form.trialType"
                    class="flex-auto"
                />

                <div class="bg-abc-gray mt-6 rounded-lg p-4">
                    <div class="lg:w-1/2">
                        <!-- Sample details -->
                        <H3Heading
                            line-height="leading-h4"
                            class="mt-10 mb-4 text-center md:text-left"
                        >
                            Sample Type
                        </H3Heading>
                        <div class="mb-8">
                            <SelectTag
                                id="sampleType"
                                v-model="form.trialType"
                                class="w-full"
                                round-size="rounded-sm"
                                required
                            >
                                <option
                                    v-for="(val, key) in sampleTypeOptions"
                                    :key="key"
                                    :value="key"
                                >
                                    {{ val }}
                                </option>
                            </SelectTag>
                        </div>

                        <H3Heading
                            line-height="leading-h4"
                            class="mb-4 text-center md:text-left"
                        >
                            Personal details
                        </H3Heading>
                        <div class="mt-4 mb-2">
                            <LabelTag for="firstName">First Name *</LabelTag>
                            <InputTag
                                id="firstName"
                                v-model="form.firstName"
                                autocomplete="on"
                                class="w-full rounded-sm"
                                required
                            />
                        </div>
                        <div class="mt-4 mb-2">
                            <LabelTag for="lastName">Last Name *</LabelTag>
                            <InputTag
                                id="lastName"
                                v-model="form.lastName"
                                autocomplete="on"
                                class="w-full rounded-sm"
                                required
                            />
                        </div>
                        <div class="mt-4 mb-2">
                            <LabelTag for="email">Email *</LabelTag>
                            <InputTag
                                id="email"
                                v-model="form.email"
                                type="email"
                                autocomplete="on"
                                class="w-full rounded-sm"
                                required
                            />
                        </div>
                        <div class="mt-4 mb-2">
                            <LabelTag for="phone">Phone *</LabelTag>
                            <InputTag
                                id="phone"
                                v-model="form.phone"
                                type="tel"
                                autocomplete="on"
                                class="w-full rounded-sm"
                                required
                            />
                        </div>

                        <!-- Organization details -->
                        <H3Heading
                            line-height="leading-h4"
                            class="mt-10 mb-4 text-center md:text-left"
                        >
                            Organization details
                        </H3Heading>
                        <div class="mt-4 mb-2">
                            <LabelTag for="orgName">Organization name *</LabelTag>
                            <InputTag
                                id="orgName"
                                v-model="form.organization.name"
                                autocomplete="on"
                                class="w-full rounded-sm"
                                required
                            />
                        </div>
                        <div class="mt-4 mb-2">
                            <LabelTag for="orgStreet">Street Address *</LabelTag>
                            <InputAddressTag
                                id="orgStreet"
                                v-model:street="form.organization.street"
                                v-model:city="form.organization.city"
                                v-model:state="form.organization.state"
                                v-model:zip="form.organization.zip"
                                class="w-full rounded-sm"
                                placeholder="Enter test"
                                required
                            />
                        </div>
                        <div class="mt-4 mb-2 md:flex">
                            <div class="flex-1 md:pr-4">
                                <LabelTag for="orgCity">City *</LabelTag>
                                <InputTag
                                    id="orgCity"
                                    v-model="form.organization.city"
                                    autocomplete="on"
                                    class="w-full rounded-sm"
                                    required
                                />
                            </div>
                            <div class="mt-4 flex-none md:mt-0 md:w-52 md:pr-4">
                                <LabelTag for="orgState">State *</LabelTag>
                                <SelectTag
                                    id="orgState"
                                    v-model="form.organization.state"
                                    autocomplete="on"
                                    class="w-full"
                                    round-size="rounded-sm"
                                    required
                                >
                                    <option
                                        v-for="(val, key) in STATES"
                                        :key="key"
                                        :value="key"
                                    >
                                        {{ val }}
                                    </option>
                                </SelectTag>
                            </div>
                            <div class="mt-4 flex-none md:mt-0 md:w-32">
                                <LabelTag for="orgZip">ZIP Code *</LabelTag>
                                <InputTag
                                    id="orgZip"
                                    v-model="form.organization.zip"
                                    autocomplete="on"
                                    class="w-full rounded-sm"
                                    required
                                />
                            </div>
                        </div>

                        <!-- Referral details -->
                        <H3Heading
                            line-height="leading-h4"
                            class="mt-10 mb-4 text-center md:text-left"
                        >
                            Referral details
                        </H3Heading>
                        <div class="mt-4 mb-2">
                            <LabelTag for="referral">Referral information</LabelTag>
                            <InputTag
                                id="referral"
                                v-model="form.referral"
                                class="w-full rounded-sm"
                            />
                        </div>
                        <div class="mt-4 mb-2">
                            <LabelTag for="comments">Comments</LabelTag>
                            <TextareaTag
                                id="comments"
                                v-model="form.comments"
                                class="w-full rounded-sm"
                            />
                        </div>
                    </div>

                    <div class="mt-8">
                        <CheckboxTag
                            v-model="form.agree"
                            :value="true"
                            :required="true"
                            line-height="leading-tight"
                            class="mb-4"
                        >
                            I agree to the terms and conditions of the ABC free trial
                        </CheckboxTag>
                        <CheckboxTag
                            v-model="form.consent_to_receive_email"
                            :value="true"
                            line-height="leading-tight"
                        >
                            I consent to receive periodic emails and promotion from American Book Company.
                        </CheckboxTag>
                    </div>
                    <H3Heading
                        line-height="leading-h4"
                        class="mt-6 mb-4 text-center md:text-left"
                    >
                        Order total: FREE
                    </H3Heading>
                    <ul class="mb-6">
                        <li
                            v-if="
                                form.trialType?.toLowerCase().includes('digital') ||
                                form.trialType?.toLowerCase().includes('both')
                            "
                        >
                            1 code for access to eBook and online testing through CourseWave
                        </li>
                        <li
                            v-if="
                                form.trialType?.toLowerCase().includes('print') ||
                                form.trialType?.toLowerCase().includes('both')
                            "
                        >
                            1 print preview workbook
                        </li>
                    </ul>

                    <div
                        v-if="errorMsg"
                        class="mt-8 text-center"
                        v-html="errorMsg"
                    ></div>
                    <ButtonPrimary
                        type="submit"
                        :disabled="processing"
                        class="my-8 px-24"
                        >Start my free trial
                    </ButtonPrimary>
                </div>
            </div>
        </form>
    </div>
</template>
